// Cabinet-class facade.
//
// As of the parametric-classes refactor, the three system classes
// (BaseCabinet, WallCabinet, TallCabinet) are no longer hand-written
// TypeScript. They are JSON `ParametricCabinetClass` documents that
// live in `dsl/seed.ts` and are mirrored into the `cabinet_classes`
// ddb collection at runtime. The evaluator in `dsl/evaluate.ts` walks
// those documents to produce the same `CabinetMeshChunk[]` /
// `CabinetPart[]` / `DrillPattern` shapes consumers expect.
//
// This file keeps the public surface stable:
//   • CabinetClass     — interface; same shape as before
//   • getCabinetClass  — synchronous registry lookup
//   • CABINETS         — snapshot of the embedded seed
//   • Dims / Anchor / Category — re-exported types
//
// For ddb-reactive consumers (CabinetPalette, class authoring UI) use
// `useCabinetClasses()` from `dsl/registry.ts`.

import type { CabinetMeshChunk, CabinetPart } from "@/lib/types";
import { SYSTEM_CLASSES } from "./dsl/seed";
import { fromParametric } from "./dsl/adapter";
import {
  getCabinetClass as getFromRegistry,
  getCabinetClasses as getRegistrySnapshot,
} from "./dsl/registry";

export type Anchor = "floor" | "wall" | "ceiling";
export type Category = "base" | "wall" | "tall";

export interface Dims { w: number; h: number; d: number; }

/** One drilled hole (or, when `kind: "groove"`, a routed slot — see the
 *  `endX/endY/width` fields below).  Coordinates measured on the drilled
 *  face of a part: origin is the part's bottom-left corner as it sits on
 *  the saw, drilled face up. All distances in inches. */
export interface DrillEntry {
  x: number;
  y: number;
  diameter: number;
  depth: number;
  /** `inside` = carcass-interior face of a side panel. `back` = back face of
   *  a door (where hinge cups go). Downstream MPR/CNC postprocessors use
   *  this to pick the right setup. */
  face: "inside" | "back";
  /** Tool family — postprocessors map to tool list ([200] in MPR). The
   *  standard library emits `shelf_pin`, `hinge_cup`, and `groove` (back-
   *  panel rebate); user-added drillings from PartOverride.extraDrillings
   *  widen to screw/cable/custom. */
  kind: "shelf_pin" | "hinge_cup" | "screw" | "cable" | "custom" | "groove";
  /** For `kind: "groove"` only: end point of the routed path. `x,y` is
   *  the start. `diameter` is reused as the bit width (i.e. the slot
   *  width perpendicular to the routing direction). */
  endX?: number;
  endY?: number;
}

/** Drillings keyed by part id (matches Cabinet.parts()'s ids). */
export type DrillPattern = Record<string, DrillEntry[]>;

export interface CabinetClass {
  id: string;
  label: string;
  category: Category;
  anchor: Anchor;
  defaults: Dims;
  bounds: { w: [number, number]; h: [number, number]; d: [number, number] };
  /** Snap dims to permitted range; subclasses may also snap to module sizes. */
  clamp(dims: Dims): Dims;
  mesh(dims: Dims): CabinetMeshChunk[];
  parts(dims: Dims): CabinetPart[];
  /** 32mm-system drilling per part. */
  drillPattern(dims: Dims): DrillPattern;
}

// ── Registry surface ────────────────────────────────────────────

/** Synchronous lookup. Pure helpers (validation, BOM, exporters) call
 *  this. Returns the registry's view (which may have been augmented
 *  from ddb via `useCabinetClasses()`). */
export function getCabinetClass(id: string): CabinetClass | null {
  return getFromRegistry(id);
}

/** Snapshot of the embedded system classes — stable across reloads,
 *  does NOT include user-authored ddb classes. Useful for tests and
 *  unit-style helpers. For the live list (ddb + embedded), use
 *  `useCabinetClasses()` from `./dsl/registry`. */
export const CABINETS: CabinetClass[] = SYSTEM_CLASSES.map(fromParametric);
export const CABINET_REGISTRY: Record<string, CabinetClass> = Object.fromEntries(
  CABINETS.map((c) => [c.id, c]),
);

/** Live snapshot from the runtime registry — call this when you want
 *  the ddb-augmented list synchronously (e.g. inside an effect). */
export const getAllCabinetClasses = getRegistrySnapshot;

// ── Direct class re-exports (back-compat for callers that imported by name) ──

export const BaseCabinet = CABINET_REGISTRY["BaseCabinet"]!;
export const WallCabinet = CABINET_REGISTRY["WallCabinet"]!;
export const TallCabinet = CABINET_REGISTRY["TallCabinet"]!;
